import {useAppContextStore} from "@store/AppContextStore";

export type Lang = "en" | "it" | "fr" | "de" | "es" | "ro" | "sq";
export const LangNumberFormat: Record<Lang, string> = {
    en: "en",
    it: "it",
    fr: "fr",
    de: "de",
    es: "es",
    ro: "ro",
    sq: "sq",
} as const;

export const SUPPORTED_LANGS: Lang[] = ["en", "it", "fr", "de", "es", "ro", "sq"];
export const DEFAULT_LANG: Lang = "en";

const STORAGE_LANG = "app.public.lang";
const STORAGE_SOURCE = "app.public.lang.source";
const QUERY_PARAM = "lang";

export type LangSource = "picker" | "query" | "browser";

export function isSupportedLang(x: string | null | undefined): x is Lang {
    return !!x && (SUPPORTED_LANGS as string[]).includes(x);
}

export function normalizeBrowserLang(raw: string | undefined | null): Lang | null {
    if (!raw) return null;
    const base = raw.toLowerCase().split("-")[0];
    return isSupportedLang(base) ? (base as Lang) : null;
}

export function detectLangFromLocation(): Lang | null {
    try {
        const url = new URL(window.location.href);
        const q = url.searchParams.get(QUERY_PARAM);
        return isSupportedLang(q ?? "") ? (q as Lang) : null;
    } catch {
        return null;
    }
}

export function readUserLang(): Lang {
    try {
        const v = window.localStorage.getItem(STORAGE_LANG);
        return v && isSupportedLang(v ?? "") ? v as Lang : DEFAULT_LANG;
    } catch {
        return DEFAULT_LANG;
    }
}

export function readLocaleCurrency(): string {
    const {language = DEFAULT_LANG} = useAppContextStore.getState();
    return LangNumberFormat[language as Lang] ?? DEFAULT_LANG;
}

export function readLocale(language?: Lang, country2?: string): string {
    const lang = language ?? useAppContextStore.getState().language ?? DEFAULT_LANG;
    const cc = (country2 ?? "").trim().toUpperCase();

    if (!cc) return lang;

    return `${lang}-${cc}`;
}

export function readPickerLang(): Lang | null {
    try {
        const source = window.localStorage.getItem(STORAGE_SOURCE);
        if (source !== "picker") return null;
        const v = window.localStorage.getItem(STORAGE_LANG);
        return isSupportedLang(v ?? "") ? (v as Lang) : null;
    } catch {
        return null;
    }
}

export function persistLang(lang: Lang, source: LangSource) {
    try {
        window.localStorage.setItem(STORAGE_LANG, lang);
        window.localStorage.setItem(STORAGE_SOURCE, source);
    } catch {
    }
}

export function resolveInitialLang(): Lang {
    const fromQuery = detectLangFromLocation();
    if (fromQuery) {
        console.log("[LANG] using lang from query", fromQuery);
        persistLang(fromQuery, "query");
        return fromQuery;
    }

    const fromPicker = readPickerLang();
    if (fromPicker) {
        console.log("[LANG] using lang from picker", fromPicker);
        return fromPicker;
    }

    const browser = normalizeBrowserLang(navigator.language);
    if (browser) {
        console.log("[LANG] using browser lang", browser);

        persistLang(browser, "browser");
        return browser;
    }

    return DEFAULT_LANG;
}
